import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, Send } from "lucide-react";
import { toast } from "sonner";
import { tipDeveloper, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { TIP } from "@/constants/testIds";

const QUICK = [10, 25, 50, 100, 250];

export default function TipModal({ open, onClose, app }) {
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [amount, setAmount] = useState(25);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!user) { nav("/login"); return; }
    const coins = Number(amount);
    if (!coins || coins < 1) { toast.error("Enter a valid amount"); return; }
    if ((user.coins || 0) < coins) {
      toast.error("Not enough coins", { description: "Grab more in the Coin Store" });
      nav("/coins"); onClose(); return;
    }
    setBusy(true);
    try {
      const r = await tipDeveloper(app.slug, coins, msg);
      setUser({ ...user, coins: r.coins_remaining });
      toast.success(`Sent ${coins} coins to ${app.developer}`, { description: msg ? `“${msg}”` : "Rock on 🤘" });
      onClose();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/70 backdrop-blur-sm" onClick={onClose}>
          <motion.div
            data-testid={TIP.modal}
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            className="glass-strong rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 relative border border-[#FFE300]/20"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid={TIP.close} onClick={onClose} className="absolute right-4 top-4 text-white/50 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <div className="mono text-[10px] uppercase tracking-widest text-[#FFE300] mb-1">// send love</div>
            <h3 className="font-heading font-black text-2xl uppercase tracking-tighter">
              Tip {app.developer}
            </h3>
            <div className="flex items-center gap-3 mt-3">
              <img src={app.icon_url} className="w-10 h-10 rounded-lg" alt="" />
              <div>
                <div className="font-bold text-sm">{app.name}</div>
                <div className="mono text-[10px] text-white/50">{app.developer}</div>
              </div>
              <div className="ml-auto mono text-xs text-[#FFE300] flex items-center gap-1">
                <Coins className="w-3.5 h-3.5" /> {user?.coins ?? 0}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-5 gap-2">
              {QUICK.map((q) => (
                <button key={q} data-testid={`${TIP.quick}-${q}`} onClick={() => setAmount(q)} className={`mono text-xs px-2 py-2 rounded-full border transition-colors ${amount === q ? "bg-[#FFE300] text-black border-[#FFE300]" : "border-white/10 text-white/80 hover:border-[#FFE300]/60"}`}>
                  {q}
                </button>
              ))}
            </div>

            <label className="block mt-4">
              <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Custom amount</div>
              <input data-testid={TIP.amountInput} type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE300]/60" />
            </label>

            <label className="block mt-3">
              <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Message (optional)</div>
              <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={140} placeholder="Keep making magic…"
                className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#FFE300]/60" />
            </label>

            <button data-testid={TIP.submit} onClick={send} disabled={busy}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FFE300] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : `Tip ${amount} Coins`}
            </button>
            <p className="mono text-[10px] text-white/40 text-center mt-3">
              Coins are non-refundable · Developer receives payout after review
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
